import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { Evaluator } from '../lib/types';
import posthog from '../lib/posthog';
import { useRealtimeSync } from '../lib/hooks/useRealtimeSync';

interface EvaluatorContextType {
  evaluators: Evaluator[];
  setEvaluators: React.Dispatch<React.SetStateAction<Evaluator[]>>;
  createEvaluator: (
    evaluator: Omit<
      Evaluator,
      'id' | 'created_at' | 'updated_at' | 'user_id' | 'is_example'
    >,
  ) => Promise<Evaluator>;
  updateEvaluator: (
    id: string,
    evaluator: Partial<Evaluator>,
  ) => Promise<Evaluator | null>;
  deleteEvaluator: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const EvaluatorContext = createContext<EvaluatorContextType | undefined>(
  undefined,
);

export function EvaluatorProvider({ children }: { children: React.ReactNode }) {
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const workspaceId = user?.currentWorkspace?.workspace_id;

  const fetchEvaluators = async () => {
    try {
      if (!user) {
        setEvaluators([]);
        setError(null);
        setLoading(false);
        return;
      }

      if (!workspaceId) {
        if (user.workspace_profiles == null) {
          setError(null);
          setLoading(true);
          return;
        }

        throw new Error('Workspace ID missing from user');
      }

      setLoading(true);
      const { data, error } = await supabase
        .from('evaluators')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setError(null);
      setEvaluators(data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch evaluators',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvaluators();
  }, [user, workspaceId]);

  useRealtimeSync('evaluators', workspaceId, fetchEvaluators);

  const createEvaluator = async (
    evaluatorData: Omit<
      Evaluator,
      'id' | 'created_at' | 'updated_at' | 'user_id' | 'is_example'
    >,
  ) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { data, error } = await supabase
        .from('evaluators')
        .insert([
          {
            ...evaluatorData,
            is_example: false,
            user_id: user?.id,
            workspace_id: workspaceId,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      posthog.capture('evaluator_created', {
        evaluator_id: data.id,
        workspace_id: workspaceId,
      });

      setEvaluators((current) => [data, ...current]);
      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to create evaluator',
      );
    }
  };

  const updateEvaluator = async (
    id: string,
    evaluatorData: Partial<Evaluator>,
  ): Promise<Evaluator | null> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { data, error } = await supabase
        .from('evaluators')
        .update(evaluatorData)
        .eq('id', id)
        .eq('workspace_id', workspaceId)
        .select()
        .single();

      if (error) throw error;

      posthog.capture('evaluator_updated', {
        evaluator_id: id,
        workspace_id: workspaceId,
      });

      setEvaluators((current) =>
        current.map((evaluator) =>
          evaluator.id === id ? { ...evaluator, ...data } : evaluator,
        ),
      );
      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to update evaluator',
      );
    }
  };

  const deleteEvaluator = async (id: string) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { error } = await supabase
        .from('evaluators')
        .delete()
        .eq('id', id)
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      posthog.capture('evaluator_deleted', {
        evaluator_id: id,
        workspace_id: workspaceId,
      });

      setEvaluators((current) =>
        current.filter((evaluator) => evaluator.id !== id),
      );
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to delete evaluator',
      );
    }
  };

  return (
    <EvaluatorContext.Provider
      value={{
        evaluators,
        setEvaluators,
        createEvaluator,
        updateEvaluator,
        deleteEvaluator,
        loading,
        error,
      }}
    >
      {children}
    </EvaluatorContext.Provider>
  );
}

export function useEvaluators() {
  const context = useContext(EvaluatorContext);
  if (context === undefined) {
    throw new Error('useEvaluators must be used within an EvaluatorProvider');
  }
  return context;
}
